import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { NavigationStart, Router, RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { NgxPermissionsModule } from 'ngx-permissions';
import {
  InsightsService,
  LicenseService,
  PermissionResolverService,
  ProductFruitsService,
  RolePermissions,
  TrackingEvent,
  NavShellService,
} from 'shared/domain';
import { IconDirective, TooltipDirective, UserInfoComponent } from 'shared/ui-common';
import { IsMobileService, OrganisationSettingsService, TreetopPropertiesProvider } from 'shared/util-common';

import { GuidelineState } from '@treetop/shared/model';
import { SearchComponent } from '@treetop/shared/tt-search';

import { GuidelineSyncPopoverComponent } from './guideline-sync-popover/guideline-sync-popover.component';
import { GuidelineSyncService } from '../../../../../libs/shared/util-common/src/lib/services/guideline-sync.service';
import { NetworkStatusService } from '../../../../../libs/shared/util-common/src/lib/services/network-status.service';
import { SmartbookSidebarService } from '../../../../../libs/smartbook-page/src/lib/smartbook-sidebar.service';

@Component({
  standalone: true,
  selector: 'tt-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    NgxPermissionsModule,
    TranslocoModule,
    RouterModule,
    MatIconModule,
    UserInfoComponent,
    IconDirective,
    SearchComponent,
    TooltipDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'data-testid': 'tt-header',
  },
})
export class HeaderComponent implements AfterViewInit {
  protected licenseService = inject(LicenseService);
  protected navShellService = inject(NavShellService);
  protected smartbookSidebarService = inject(SmartbookSidebarService);
  protected isMobileService = inject(IsMobileService);
  protected permissionResolverService = inject(PermissionResolverService);
  protected insightsService = inject(InsightsService);
  protected elementRef = inject(ElementRef<HTMLElement>);
  protected productFruitsService = inject(ProductFruitsService);
  private router = inject(Router);
  private networkStatusService = inject(NetworkStatusService);
  private guidelineSyncService = inject(GuidelineSyncService);
  private organisationSettingsService = inject(OrganisationSettingsService);

  protected isStartedOnlineAndCurrentlyOnline = computed(() => {
    const defaultOnline = this.networkStatusService.defaultOnline();
    const isOnline = this.networkStatusService.isOnline();

    return defaultOnline && isOnline;
  });

  protected syncingVm = computed(() => {
    const syncing = this.guidelineSyncService.syncStatus().syncing;
    const progress = this.guidelineSyncService.progress();
    const offlineSyncEnabled = this.organisationSettingsService.settings()?.offlineSettings?.allowOfflineSync;

    return { syncing, progress, offlineSyncEnabled };
  });

  private helpCenterButton = viewChild('helpCenterButton', { read: ElementRef });
  private isSmartbookGuidelineViewPage: WritableSignal<boolean>;

  protected isQaEnvironment =
    TreetopPropertiesProvider.properties.baseUrl === 'https://treetop-clinic-qa.azurewebsites.net/';
  protected environmentVersion = TreetopPropertiesProvider.properties.version ?? 'unknown';

  protected showDashboardInsteadOfMenu = computed(() => {
    const isSmartbookGuidelineViewPage = this.isSmartbookGuidelineViewPage();
    const isMobile = this.isMobileService.isMobile();

    if (!isMobile) {
      return true;
    }

    return !isSmartbookGuidelineViewPage;
  });

  constructor() {
    this.isSmartbookGuidelineViewPage = signal(this.isUrlASmartbookPage(this.router.url));

    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationStart) {
        const isUrlASmartbookPage = this.isUrlASmartbookPage(event.url);
        this.isSmartbookGuidelineViewPage.set(isUrlASmartbookPage);
      }
    });

    effect(() => {
      const ready = this.productFruitsService.ready();
      const button = this.helpCenterButton();
      if (ready && button) {
        this.attachHelpCenter();
      }
    });
  }

  public ngAfterViewInit(): void {
    const height = this.elementRef.nativeElement.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--tt-header-height', `${height}px`);
  }

  protected onHubClick(): void {
    this.insightsService.trackEvent(TrackingEvent.HUB_CLICKED);
  }

  protected onStatisticsClick(): void {
    this.insightsService.trackEvent(TrackingEvent.STATISTICS_CLICKED);
  }

  private isUrlASmartbookPage(url: string): boolean {
    const regex = /^\/smartbook(\/.*)?$/;
    return regex.test(url) || url === '/';
  }

  private attachHelpCenter(): void {
    this.productFruitsService.attachHelpCenterButton(this.helpCenterButton().nativeElement);
  }

  protected permissions = RolePermissions;
  protected readonly GuidelineState = GuidelineState;
  protected readonly TreetopPropertiesProvider = TreetopPropertiesProvider;
  protected readonly GuidelineSyncPopoverComponent = GuidelineSyncPopoverComponent;
}
